import React from 'react';
import { Link } from 'react-router-dom';
import Badge from '../ui/Badge';
import EmptyState from '../ui/EmptyState';
import { topAttentionDomains, attentionBadges, attentionSummary } from '../domainMetrics';

/* Maps the attention counter keys onto the shared badge palette. */
const BADGE_STATE = { drafts: 'draft', noDesc: 'warn', imgErr: 'danger' };

/* One badge per non-zero counter; shared by this card and the table column. */
export const AttentionBadges = ({ domain }) => {
  const badges = attentionBadges(domain);
  if (badges.length === 0) return <span className="muted">—</span>;
  return (
    <span style={{ display: 'inline-flex', gap: '4px', flexWrap: 'wrap' }}>
      {badges.map((b) => (
        <Badge key={b.key} state={BADGE_STATE[b.key]}>
          {b.count} {b.label}
        </Badge>
      ))}
    </span>
  );
};

/* "Needs attention" — the worst n sites by flagged-page count, each a link
   to its detail page. */
export default function AttentionCard({ domains, limit = 8, onSelect }) {
  const top = topAttentionDomains(domains, limit);
  const flagged = domains.filter((d) => attentionBadges(d).length > 0).length;

  return (
    <div className="card" style={{ margin: 0 }}>
      <div className="card-header">
        <h2 className="card-title">Needs attention</h2>
        {flagged > 0 && (
          <span className="muted small">
            {flagged > top.length ? `top ${top.length} of ${flagged}` : `${flagged} site${flagged === 1 ? '' : 's'}`}
          </span>
        )}
      </div>
      {top.length === 0 ? (
        <EmptyState
          title="All clear"
          message="No drafts, missing descriptions, or image errors since the last page-stats refresh."
        />
      ) : (
        <div style={{ maxHeight: '320px', overflowY: 'auto' }}>
          {top.map((domain) => (
            <Link
              key={domain.id}
              to={`/domain/${domain.id}`}
              onClick={() => onSelect && onSelect(domain)}
              title={attentionSummary(domain)}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '8px',
                padding: '6px',
                borderRadius: '6px',
                color: 'inherit',
                textDecoration: 'none',
                fontSize: '13px',
              }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
                {domain.favicon && (
                  <img src={domain.favicon} alt="" width="14" height="14" style={{ borderRadius: '2px', flexShrink: 0 }} />
                )}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {domain.hostname}
                </span>
              </span>
              <AttentionBadges domain={domain} />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
